import axios from "../axios";

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  if (!authToken) return {};
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export type ProfessionalResponse = {
  id: number | string;
  type?: "PERSON" | "COMPANY";
  name?: string;
  last_name?: string;
  razon_social?: string;
  validated?: boolean;
  username?: string;
  url_site_web?: string;
  zone?: string;
  image_profile?: string;
  phone?: string;
  skill?: Array<{ skill: { id?: number; name: string } }>;
  skills?: Array<{ id?: number; name: string }>;
  portfolio?: Array<{
    id?: number | string;
    title?: string;
    portfolio_images?: Array<{ image: string }>;
  }>;
  stars?: number;
};

export type SkillResponse = {
  id: number;
  name: string;
  users?: number;
};

export type SkillCatalogItem = {
  id: number;
  name: string;
};

type SkillCatalogNormalized = { id: number; name: string };

let skillCatalogMemoryCache: SkillCatalogNormalized[] | null = null;
let skillCatalogInflight: Promise<SkillCatalogNormalized[]> | null = null;

export type JurisdictionResponse = {
  id: number;
  name: string;
  userCount?: number;
  allCountryCount?: number;
};

export type UserFilterRol = "ALL" | "CLIENT" | "JOBBOARD" | "MARKET";

export type FilterParams = {
  skillIds?: number[];
  categoryIds?: number[];
  jurisdictionIds?: number[];
  allCountry?: boolean;
  order?: "ASC" | "DESC";
  page?: number;
  pageSize?: number;
  type?: "PERSON" | "COMPANY";
  rol?: UserFilterRol;
  validated?: boolean;
  featured?: boolean;
  name?: string;
  includeFacets?: boolean;
};

export type FilterFacetSkill = {
  id: number;
  name: string;
  users: number;
};

export type FilterFacetJurisdiction = {
  id: number;
  name: string;
  users: number;
};

export type FilterFacetCategory = {
  id: number;
  name: string;
  users: number;
};

export type FilterResponse = {
  users: ProfessionalResponse[];
  itemCount: number;
  page: number;
  take: number;
  facets?: {
    skills?: FilterFacetSkill[];
    categories?: FilterFacetCategory[];
    jurisdictions?: FilterFacetJurisdiction[];
    allCountryUsers?: number;
    validatedExplore?: { true: number; false: number };
    validatedApplied?: { true: number; false: number };
    type?: {
      person: number;
      company: number;
      unknown: number;
      total: number;
    };
    types?: {
      person: number;
      company: number;
      workers: number;
      total: number;
    };
  };
};

export const sanitizeProfessionals = (professionals: ProfessionalResponse[]) => professionals;

const normalizeSkills = (skills: SkillResponse[]) =>
  [...skills].sort((a, b) => a.name.localeCompare(b.name));

const normalizeJurisdictions = (jurisdictions: JurisdictionResponse[]) =>
  [...jurisdictions].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

const normalizeFilterResponse = (response: any): FilterResponse => {
  const users = Array.isArray(response)
    ? response
    : (response?.users ?? response?.data ?? []);
  const list = Array.isArray(users) ? users : [];
  const rawFacets = response?.facets ?? {};
  const skills = Array.isArray(rawFacets?.skills)
    ? rawFacets.skills
        .map((item: any) => ({
          id: Number(item?.id),
          name: String(item?.name ?? ""),
          users: Number(item?.users ?? 0),
        }))
        .filter((item: any) => !Number.isNaN(item.id) && item.name)
    : undefined;
  const categories = Array.isArray(rawFacets?.categories)
    ? rawFacets.categories
        .map((item: any) => ({
          id: Number(item?.id),
          name: String(item?.name ?? ""),
          users: Number(item?.users ?? 0),
        }))
        .filter((item: any) => !Number.isNaN(item.id) && item.name)
    : undefined;
  const jurisdictions = Array.isArray(rawFacets?.jurisdictions)
    ? rawFacets.jurisdictions
        .map((item: any) => ({
          id: Number(item?.id),
          name: String(item?.name ?? ""),
          users: Number(item?.users ?? 0),
        }))
        .filter((item: any) => !Number.isNaN(item.id) && item.name)
    : undefined;
  const allCountryUsers =
    typeof rawFacets?.allCountryUsers === "number" ? rawFacets.allCountryUsers : undefined;
  const normalizeValidatedFacet = (facet: any) => {
    if (!facet || typeof facet !== "object") return undefined;
    const trueCount = typeof facet.true === "number" ? facet.true : 0;
    const falseCount = typeof facet.false === "number" ? facet.false : 0;
    return { true: trueCount, false: falseCount };
  };
  const validatedExplore = normalizeValidatedFacet(rawFacets?.validatedExplore);
  const validatedApplied = normalizeValidatedFacet(rawFacets?.validatedApplied);
  const normalizeTypesFacet = (facet: any) => {
    if (!facet || typeof facet !== "object") return undefined;
    const person = typeof facet.person === "number" ? facet.person : 0;
    const company = typeof facet.company === "number" ? facet.company : 0;
    const workers = typeof facet.workers === "number" ? facet.workers : 0;
    const total = typeof facet.total === "number" ? facet.total : person + company + workers;
    return { person, company, workers, total };
  };
  const types = normalizeTypesFacet(rawFacets?.type ?? rawFacets?.types);
  return {
    users: list,
    itemCount: response?.itemCount ?? list.length,
    page: response?.page ?? 1,
    take: response?.take ?? 12,
    facets: {
      skills,
      categories,
      jurisdictions,
      allCountryUsers,
      validatedExplore,
      validatedApplied,
      types,
    },
  };
};

const buildQueryParams = (params: FilterParams) => {
  const searchParams = new URLSearchParams();
  params.skillIds?.forEach((id) => searchParams.append("skillIds", String(id)));
  params.categoryIds?.forEach((id) => searchParams.append("categoryIds", String(id)));
  params.jurisdictionIds?.forEach((id) => searchParams.append("jurisdictionIds", String(id)));
  if (params.allCountry) searchParams.append("allCountry", "true");
  if (params.type) searchParams.append("type", params.type);
  if (params.rol) searchParams.append("rol", params.rol);
  if (typeof params.validated === "boolean") searchParams.append("validated", String(params.validated));
  if (params.featured) searchParams.append("featured", "true");
  if (params.includeFacets) searchParams.append("includeFacets", "true");
  const nameTerm = params.name?.trim();
  if (nameTerm) searchParams.append("name", nameTerm);
  searchParams.append("order", params.order ?? "ASC");
  searchParams.append("page", String(params.page ?? 1));
  searchParams.append("take", String(params.pageSize ?? 12));
  return searchParams.toString();
};

export const profesionalesService = {
  fetchFeaturedUsers: async () => {
    const response = await axios.get("/user/featured", getAuthHeaders());
    const raw = response?.data;
    const list = Array.isArray(raw) ? raw : (raw?.users ?? raw?.data ?? []);
    return sanitizeProfessionals(list ?? []);
  },
  fetchSkills: async () => {
    const { data } = await axios.get<{ response: { results: SkillResponse[] } }>("/skill/AllUsers", getAuthHeaders());
    const skills = data?.response?.results ?? [];
    return normalizeSkills(skills);
  },
  fetchJurisdictions: async () => {
    const { data } = await axios.get<JurisdictionResponse[]>("/user/count/jurisdiction", getAuthHeaders());
    return normalizeJurisdictions(data ?? []);
  },
  fetchSkillCatalog: async () => {
    if (skillCatalogMemoryCache !== null) {
      return skillCatalogMemoryCache;
    }
    if (!skillCatalogInflight) {
      skillCatalogInflight = (async () => {
        const { data } = await axios.get<SkillCatalogItem[]>("/skill/list", getAuthHeaders());
        const list = (data ?? [])
          .map((item) => ({
            id: Number(item.id),
            name: String(item.name ?? "").trim(),
          }))
          .filter((s) => Number.isFinite(s.id) && s.id > 0 && s.name.length > 0);
        skillCatalogMemoryCache = list;
        return list;
      })().finally(() => {
        skillCatalogInflight = null;
      });
    }
    return skillCatalogInflight;
  },
  fetchFilteredProfessionals: async (params: FilterParams) => {
    const query = buildQueryParams(params);
    const { data } = await axios.get<FilterResponse>(`/user/filter?${query}`, getAuthHeaders());
    const normalized = normalizeFilterResponse(data ?? { users: [], itemCount: 0, page: 1, take: params.pageSize ?? 12 });
    return {
      users: sanitizeProfessionals(normalized.users),
      itemCount: normalized.itemCount,
      page: normalized.page,
      take: normalized.take,
      facets: normalized.facets,
    };
  },
};
